// "Imports waiting for review" on the Data page of a hosted project that reviews imports (task
// 2026-10-05 "imports can require a data steward's approval"; routers/data_imports.py).
// GET /api/projects/{id}/data/imports?status=submitted lists what the caller sees: a data steward
// or data admin every submitted import, anybody else their own. One import opens its review:
// the counts and problems of the preview, the XLSX report, and the diff against the database as
// it is NOW (GET .../diff: rows to insert, per row to update each cell's database and file value,
// and whether an approval would still write exactly what was submitted). A reviewer approves
// (comment optional) or rejects (comment required); the uploader withdraws instead. Nobody
// reviews their own import (the server answers 403; the buttons are not offered).
// Below, "Your imports that came back" (GET .../data/imports?mine=true): the caller's imports a
// reviewer rejected in the last REJECTED_DAYS days or an approval returned (the database changed),
// with the reviewer's comment; a returned one opens on its table's page to submit again.
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Field, Textarea } from "../../components/Shared";
import { useApi } from "../../useApi";
import { summaryText } from "./importUtils";

const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)",
                 whiteSpace: "nowrap" };
const th = { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #e5e7eb", fontSize: 12,
             background: "#f9fafb" };
const td = { padding: "6px 10px", borderTop: "1px solid #f3f4f6", fontSize: 13, verticalAlign: "top" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

function DiffTables({ diff }) {
  const columns = diff.inserts.length ? Object.keys(diff.inserts[0].values) : [];
  return (
    <>
      <p style={{ fontSize: 13, margin: "0 0 8px" }}>
        Against the database now: {diff.counts.inserts} new row(s), {diff.counts.updates} row(s) changed,
        {" "}{diff.counts.unchanged} unchanged, {diff.counts.error_rows} rejected.
      </p>
      {diff.inserts.length > 0 && (
        <div data-table-scroll role="region" aria-label="Rows to add" tabIndex={0} style={{ overflow: "auto", maxHeight: 280, marginBottom: 10 }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <caption style={{ textAlign: "left", fontSize: 13, fontWeight: 600, padding: "4px 0" }}>Rows to add</caption>
            <thead><tr><th scope="col" style={th}>Row</th>{columns.map(c => <th key={c} scope="col" style={{ ...th, ...mono }}>{c}</th>)}</tr></thead>
            <tbody>
              {diff.inserts.map(r => (
                <tr key={r.row}><td style={td}>{r.row}</td>{columns.map(c => <td key={c} style={td}>{r.values[c] ?? ""}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {diff.updates.length > 0 && (
        <div data-table-scroll role="region" aria-label="Cells to change" tabIndex={0} style={{ overflow: "auto", maxHeight: 280, marginBottom: 10 }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <caption style={{ textAlign: "left", fontSize: 13, fontWeight: 600, padding: "4px 0" }}>Cells to change</caption>
            <thead>
              <tr><th scope="col" style={th}>Row</th><th scope="col" style={th}>Key</th><th scope="col" style={th}>Column</th>
                  <th scope="col" style={th}>In the database</th><th scope="col" style={th}>After the import</th></tr>
            </thead>
            <tbody>
              {diff.updates.flatMap(u => u.changes.map(c => (
                <tr key={`${u.row}-${c.column}`}>
                  <td style={td}>{u.row}</td><td style={td}>{u.key}</td><td style={{ ...td, ...mono }}>{c.column}</td>
                  <td style={td}>{c.old ?? <span style={{ color: "#6b7280" }}>empty</span>}</td><td style={td}>{c.new}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
      {diff.truncated && <p style={{ fontSize: 12, color: "#6b7280" }}>Only the first rows of each kind are listed.</p>}
    </>
  );
}

function Review({ projectId, imp, mine, canReview, onChanged }) {
  const base = `/projects/${projectId}/data/tables/${encodeURIComponent(imp.table)}/imports/${imp.id}`;
  const diff = useApi(`${base}/diff`);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState(null);
  const run = (what, fn) => async () => {
    setBusy(what);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err);
      if (err.status === 409) onChanged();      // returned to its uploader, or reviewed meanwhile
    } finally {
      setBusy("");
    }
  };
  const p = imp.preview || {};
  const stem = imp.filename.replace(/\.[^.]+$/, "");
  const d = diff.data;
  return (
    <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12, marginTop: 8 }}>
      <ErrorNote error={error || diff.error} />
      <p style={{ fontSize: 13, margin: "0 0 8px" }}>{summaryText(p)}</p>
      {(p.issues || []).length > 0 && (
        <ul aria-label="Problems in the file" style={{ fontSize: 13, margin: "0 0 8px" }}>
          {p.issues.slice(0, 10).map((i, n) => <li key={n}>row {i.row ?? "—"}, {i.column ?? "—"}: {i.message}</li>)}
        </ul>
      )}
      {imp.on_error === "valid_only" && p.error_rows > 0 && (
        <p style={{ fontSize: 13, color: "#92400e" }}>Submitted with "valid rows only": {p.error_rows} row(s) with errors are skipped.</p>
      )}
      {diff.loading && !d && <Loading />}
      {d && d.matches_submission === false && (
        <p role="alert" style={{ fontSize: 13, color: "#b91c1c" }}>
          The database changed since the submission: an approval now would write something else, so it
          would be refused and the import returned to its uploader.
        </p>
      )}
      {d && <DiffTables diff={d} />}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Btn variant="secondary" disabled={!!busy}
             onClick={run("download", () => api.download(`${base}/report.xlsx`, `${stem}.report.xlsx`))}>
          Download XLSX report
        </Btn>
        {mine && (
          <Btn variant="danger" disabled={!!busy} onClick={run("withdraw", () => api.del(base))}>Withdraw</Btn>
        )}
      </div>
      {canReview && !mine && (
        <div style={{ marginTop: 12 }}>
          <Field label={`Comment for ${imp.uploaded_by || "the uploader"}`} hint="Required to reject.">
            <Textarea value={comment} onChange={e => setComment(e.target.value)} />
          </Field>
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn disabled={!!busy} onClick={run("approve", () => api.post(`${base}/approve`, { comment: comment || null }))}>
              {busy === "approve" ? "Approving…" : "Approve and import"}
            </Btn>
            <Btn variant="danger" disabled={!!busy || !comment.trim()}
                 onClick={run("reject", () => api.post(`${base}/reject`, { comment }))}>
              {busy === "reject" ? "Rejecting…" : "Reject"}
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

const REJECTED_DAYS = 30;

function cameBack(imp, now = Date.now()) {
  if (imp.status === "pending" && imp.review_decision === "returned") return true;
  return imp.status === "rejected" && !!imp.reviewed_at
    && now - new Date(imp.reviewed_at).getTime() < REJECTED_DAYS * 86400000;
}

function CameBack({ projectId, items }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <section aria-label="Your imports that came back">
        <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Your imports that came back</h2>
        <table className="responsive-table" style={{ borderCollapse: "collapse", width: "100%" }}>
          <caption style={srOnly}>Your imports that came back</caption>
          <thead>
            <tr><th scope="col" style={th}>File</th><th scope="col" style={th}>Table</th>
                <th scope="col" style={th}>Decision</th><th scope="col" style={th}>By</th>
                <th scope="col" style={th}>Comment</th><th scope="col" style={th}><span style={srOnly}>Action</span></th></tr>
          </thead>
          <tbody>
            {items.map(imp => (
              <tr key={imp.id}>
                <td data-label="File" style={td}>{imp.filename}</td>
                <td data-label="Table" style={{ ...td, ...mono }}>{imp.table}</td>
                <td data-label="Decision" style={td}>{imp.status === "rejected"
                  ? <Badge color="red">rejected</Badge> : <Badge color="amber">returned</Badge>}</td>
                <td data-label="By" style={td}>{imp.reviewed_by || "—"}</td>
                <td data-label="Comment" style={td}>{imp.review_comment || "—"}</td>
                <td style={td}>
                  {imp.status === "pending" && (
                    <Link to={`/projects/${projectId}/data/${encodeURIComponent(imp.table)}?import=${imp.id}`}>
                      Check {imp.filename} again
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Card>
  );
}

export default function ImportReviewPanel({ projectId, canReview }) {
  const { user } = useAuth();
  const list = useApi(`/projects/${projectId}/data/imports?status=submitted`);
  const mineList = useApi(`/projects/${projectId}/data/imports?mine=true`);
  const [open, setOpen] = useState(null);
  const items = list.error ? [] : list.data || [];
  const back = (mineList.error ? [] : mineList.data || []).filter(imp => cameBack(imp));
  if (items.length === 0 && back.length === 0) return null;    // also while loading: no spinner needed
  return (
    <>
      {back.length > 0 && <CameBack projectId={projectId} items={back} />}
      {items.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <section aria-label="Imports waiting for review">
            <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Imports waiting for review</h2>
            <table className="responsive-table" style={{ borderCollapse: "collapse", width: "100%" }}>
              <caption style={srOnly}>Imports waiting for review</caption>
              <thead>
                <tr><th scope="col" style={th}>File</th><th scope="col" style={th}>Table</th>
                    <th scope="col" style={th}>Uploaded by</th><th scope="col" style={th}>Submitted</th>
                    <th scope="col" style={th}><span style={srOnly}>Review</span></th></tr>
              </thead>
              <tbody>
                {items.flatMap(imp => {
                  const mine = user && imp.uploaded_by === user.username;
                  return [
                    <tr key={imp.id}>
                      <td data-label="File" style={td}>{imp.filename} {mine && <Badge color="blue">yours</Badge>}</td>
                      <td data-label="Table" style={{ ...td, ...mono }}>{imp.table}</td>
                      <td data-label="Uploaded by" style={td}>{imp.uploaded_by || "—"}</td>
                      <td data-label="Submitted" style={td}>{imp.submitted_at ? new Date(imp.submitted_at).toLocaleString("en") : "—"}</td>
                      <td style={td}>
                        <Btn variant="secondary" onClick={() => setOpen(open === imp.id ? null : imp.id)}>
                          {open === imp.id ? "Close" : (canReview && !mine ? `Review ${imp.filename}` : `Show ${imp.filename}`)}
                        </Btn>
                      </td>
                    </tr>,
                    open === imp.id && (
                      <tr key={`${imp.id}-review`}>
                        <td colSpan={5} style={td}>
                          <Review projectId={projectId} imp={imp} mine={mine} canReview={canReview}
                                  onChanged={() => { setOpen(null); list.reload(); }} />
                        </td>
                      </tr>
                    ),
                  ].filter(Boolean);
                })}
              </tbody>
            </table>
          </section>
        </Card>
      )}
    </>
  );
}
